// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useToast } from '@/contexts/toast-context';
import { MergeItemParameters, mergeItem } from '@/items/api/merge-item';
import { Box } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';

interface UseMergeItemResult {
  mergeItems: (itemKey: string, parameters: MergeItemParameters) => void;
  isLoading: boolean;
  error: string | null;
  showSnackbar: boolean;
  clearError: () => void;
}

export function useMergeItem(onSuccess: () => void): UseMergeItemResult {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const mutation = useMutation({
    mutationFn: ({
      itemKey,
      parameters,
    }: {
      itemKey: string;
      parameters: MergeItemParameters;
    }) => mergeItem(itemKey, parameters),
    onError: (error: Error) => {
      // Curated 4xx-class error → inline message. Anything else
      // (unmapped 4xx, 5xx, non-axios) → snackbar so backend text
      // never leaks into the UI.
      if (error instanceof AxiosError) {
        const apiError = handleAPIError(error);
        const curated = getUserFacingErrorMessage(apiError);
        if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
          setError(curated);
          setShowSnackbar(false);
          return;
        }
        if (!apiError.sentToSentry) {
          Sentry.captureException(error);
        }
      } else {
        Sentry.captureException(error);
      }
      setError(null);
      setShowSnackbar(true);
    },
    onSettled: () => {
      // Reset the mutation state after any response (success or error)
      // This ensures the button re-enables after the request completes
      setTimeout(() => {
        mutation.reset();
      }, 100);
    },
    onSuccess: () => {
      // Invalidate queries immediately to refresh the merged item data
      queryClient.invalidateQueries({ queryKey: ['item'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });

      showToast({
        duration: 5000,
        element: <Box>Merge complete, now viewing the merged record</Box>,
      });
      onSuccess();
    },
  });

  const mergeItems = (itemKey: string, parameters: MergeItemParameters) => {
    mutation.mutate({ itemKey, parameters });
  };

  const clearError = () => {
    setError(null);
    setShowSnackbar(false);
    mutation.reset();
  };

  return {
    mergeItems,
    isLoading: mutation.isPending,
    error,
    showSnackbar,
    clearError,
  };
}
